import React, { useEffect } from 'react';
import ValidationStepPanel from './ValidationStepPanel';
import useValidationStep from './useValidationStep';
import { useReadOnly } from '../../../contexts/WizardAccessContext';
import type { EndUserHandoffView } from '../../../hooks/useEndUserHandoff';
import type { RunMarker } from '../../../types/bom.types';
import {
  AISuggestion,
  Alternative,
  ComponentAlternative,
  ComponentWithHistory,
  SuggestedComponent,
  ValidationResult,
} from './types';

/**
 * Connected step 3b: binds the hosting step's analysis data + API handlers
 * to the validation hook and hands the derived state to the presentational
 * panel. This is the only validation file the BOM step imports.
 */
export interface ValidationStepProps {
  ruleResults: ValidationResult[];
  componentAlternatives: ComponentAlternative[];
  aiSuggestion: AISuggestion | null;
  components: ComponentWithHistory[];
  /** Server-persisted per-finding verdicts, seeded into local review state. */
  reviewStates?: Record<string, 'applied' | 'skipped'> | null;
  /** Persists verdict changes (merge; null deletes). Fire-and-forget. */
  onPersistReviewState?: (states: Record<string, 'applied' | 'skipped' | null>) => void;
  onRunValidation: () => Promise<void>;
  /** The persisted validation marker; a RUNNING one is waited on, never re-run. */
  validationRun?: RunMarker | null;
  onAwaitValidation?: () => Promise<void>;
  /** Tells the host the check is running, for Robbie's panel. */
  onRunningChange?: (running: boolean) => void;
  /** The check failed and its error card is up — Robbie's panel says so too. */
  onFailedChange?: (failed: boolean) => void;
  onReplaceComponent: (componentIndex: number, alternative: Alternative) => Promise<void>;
  onRevertComponent: (componentIndex: number, targetVersionIndex: number) => Promise<void>;
  onAddComponent: (component: SuggestedComponent) => Promise<void>;
  onApplySuggestion: (field: string, value: string) => Promise<void>;
  onNext: () => void;
  onPrevious: () => void;
  isNextLoading?: boolean;
  onSubStepSelect?: (id: string) => void;
  /** End-user handoff view — banner, footer CTA swap, and read-only gating. */
  handoff?: EndUserHandoffView;
}

export const ValidationStep: React.FC<ValidationStepProps> = ({
  ruleResults,
  componentAlternatives,
  aiSuggestion,
  components,
  reviewStates,
  onPersistReviewState,
  onRunValidation,
  validationRun,
  onAwaitValidation,
  onRunningChange,
  onFailedChange,
  onReplaceComponent,
  onRevertComponent,
  onAddComponent,
  onApplySuggestion,
  onNext,
  onPrevious,
  isNextLoading,
  onSubStepSelect,
  handoff,
}) => {
  // Two read-only sources compose: the role model (viewer) and the handoff
  // (end user outside their window). The open fill window overrides both —
  // the pending end user is technically a VIEWER, yet resolving these checks
  // is exactly what was handed to them. One effective flag feeds the hook,
  // which guards every mutator and hands it to the panel and cards.
  const viewerReadOnly = useReadOnly();
  const readOnly = handoff?.canFill ? false : viewerReadOnly || (handoff?.readOnly ?? false);
  const state = useValidationStep({
    ruleResults,
    componentAlternatives,
    components,
    serverReviewStates: reviewStates,
    onPersistReviewState,
    onRunValidation,
    validationRun,
    onAwaitValidation,
    onReplaceComponent,
    onRevertComponent,
    onAddComponent,
    onApplySuggestion,
    readOnly,
  });

  useEffect(() => {
    onRunningChange?.(state.isRunning);
    return () => onRunningChange?.(false);
  }, [state.isRunning, onRunningChange]);
  const checkFailed = Boolean(state.error) && !state.isRunning;
  useEffect(() => {
    onFailedChange?.(checkFailed);
    return () => onFailedChange?.(false);
  }, [checkFailed, onFailedChange]);

  return (
    <ValidationStepPanel
      state={state}
      aiSuggestion={aiSuggestion}
      onBack={onPrevious}
      onContinue={onNext}
      isContinueLoading={isNextLoading}
      onSubStepSelect={onSubStepSelect}
      handoff={handoff}
    />
  );
};

export default ValidationStep;
